import React, { useState, useEffect } from 'react';
import { Usuario, PermissaoUsuario } from '../../../../types/admin';
import { apiService } from '../../../../services/api';
import { modalUtils } from '../../../../utils/modalUtils';
import { API_BASE_URL } from '../../../../environment';

interface Props {
  usuario: Usuario;
  onClose: () => void;
  onRefresh: () => void;
  onAbrirExcecoes?: (usuario: Usuario) => void;
}

export const ModalDetalhesUsuario: React.FC<Props> = ({ usuario, onClose, onRefresh, onAbrirExcecoes }) => {
  const [activeTab, setActiveTab] = useState<'perfil' | 'excecoes'>('perfil');
  const [excecoes, setExcecoes] = useState<PermissaoUsuario[]>([]);
  const [loadingExcecoes, setLoadingExcecoes] = useState(false);
  const [perfilUsuario, setPerfilUsuario] = useState<any>(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);

  // URL base da API (removido /api/v1 para usar apenas o domínio base)
  const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');

  // Carregar detalhes do perfil do usuário
  const fetchPerfilUsuario = async () => {
    try {
      setLoadingPerfil(true);
      const response = await apiService.getPerfilUsuario(usuario.id);
      setPerfilUsuario(response.data);
    } catch (error) {
      console.error('Erro ao buscar perfil do usuário:', error);
    } finally {
      setLoadingPerfil(false);
    }
  };

  // Carregar exceções do usuário
  const fetchExcecoes = async () => {
    try {
      setLoadingExcecoes(true);
      const response = await apiService.getPermissoesUsuario({ id_usuario: usuario.id });
      setExcecoes(response.data);
    } catch (error) {
      console.error('Erro ao buscar exceções do usuário:', error);
    } finally {
      setLoadingExcecoes(false);
    }
  };

  // Remover perfil do usuário
  const handleRemoverPerfil = async () => {
    if (!window.confirm('Tem certeza que deseja remover o perfil deste usuário?')) return;

    try {
      await apiService.deletePerfilUsuario(usuario.id);
      window.alert('Perfil removido com sucesso!');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Erro ao remover perfil:', error);
      window.alert('Erro ao remover perfil. Tente novamente.');
    }
  };

  // Remover exceção
  const handleRemoverExcecao = async (excecaoId: number) => {
    if (!window.confirm('Tem certeza que deseja remover esta exceção?')) return;

    try {
      await apiService.deletePermissaoUsuario(excecaoId);
      fetchExcecoes(); // Recarregar exceções
      window.alert('Exceção removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover exceção:', error);
      window.alert('Erro ao remover exceção. Tente novamente.');
    }
  };

  useEffect(() => {
    fetchPerfilUsuario();
    fetchExcecoes();
  }, [usuario.id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="modal fade" id="modal_detalhes_usuario" tabIndex={-1} role="dialog" aria-hidden="true">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <h5 className="modal-title">
              👤 {usuario.nome} {usuario.sobrenome} - Detalhes
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="modal-body">
            {/* Navigation Tabs */}
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'perfil' ? 'active' : ''}`}
                  onClick={() => setActiveTab('perfil')}
                >
                  <i className="ti ti-user-circle me-2"></i>
                  Perfil
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'excecoes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('excecoes')}
                >
                  <i className="ti ti-alert-triangle me-2"></i>
                  Exceções ({excecoes.length})
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === 'perfil' && (
              <div className="tab-pane active">
                <div className="row">
                  <div className="col-md-4">
                    {/* Info do Usuário */}
                    <div className="card">
                      <div className="card-body text-center">
                        <div className="avatar avatar-xl mx-auto mb-3">
                          {usuario.foto_perfil_url ? (
                            <img 
                              src={BASE_DOMAIN + usuario.foto_perfil_url} 
                              alt={usuario.nome}
                              className="avatar-img rounded-circle"
                            />
                          ) : (
                            <div className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                              <i className="ti ti-user fs-2"></i>
                            </div>
                          )}
                        </div>
                        <h5>{usuario.nome} {usuario.sobrenome}</h5>
                        <p className="text-muted mb-2">{usuario.email}</p>
                        {usuario.cargo && (
                          <p className="text-muted mb-2">{usuario.cargo}</p>
                        )}
                        <div className="mb-3">
                          {usuario.ativo ? (
                            <span className="badge bg-success">Ativo</span>
                          ) : (
                            <span className="badge bg-danger">Inativo</span>
                          )}
                        </div>
                        
                        <div className="text-start">
                          <div className="mb-2">
                            <small className="text-muted">Data de Criação:</small><br />
                            <span>{formatDate(usuario.data_criacao)}</span>
                          </div>
                          {usuario.telefone_celular && (
                            <div className="mb-2">
                              <small className="text-muted">Telefone:</small><br />
                              <span>{usuario.telefone_celular}</span>
                            </div>
                          )}
                          {usuario.cpf && (
                            <div className="mb-2">
                              <small className="text-muted">CPF:</small><br />
                              <span>{usuario.cpf}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-8">
                    {/* Perfil Atual */}
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">📋 Perfil Atual</h6>
                      </div>
                      <div className="card-body">
                        {loadingPerfil ? (
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm" role="status"></div>
                            <span className="ms-2">Carregando...</span>
                          </div>
                        ) : perfilUsuario && perfilUsuario.id_perfil_base ? (
                          <div>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div>
                                <span 
                                  className="badge fs-6 px-3 py-2"
                                  style={{ 
                                    backgroundColor: perfilUsuario.perfil_cor || '#6B7280',
                                    color: '#fff'
                                  }}
                                >
                                  {perfilUsuario.perfil_nome}
                                </span>
                                <p className="text-muted mt-2 mb-0">
                                  {perfilUsuario.perfil_descricao || 'Sem descrição'}
                                </p>
                              </div>
                              <div>
                                <button
                                  className="btn btn-outline-warning btn-sm me-2"
                                  onClick={() => {
                                    // Aqui você pode abrir o modal de alterar perfil
                                    onClose();
                                    // Implementar lógica para abrir modal de alteração
                                  }}
                                >
                                  <i className="ti ti-edit me-1"></i>
                                  Alterar
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={handleRemoverPerfil}
                                >
                                  <i className="ti ti-trash me-1"></i>
                                  Remover
                                </button>
                              </div>
                            </div>
                            
                            <div className="alert alert-info">
                              <i className="ti ti-info-circle me-2"></i>
                              <strong>Permissões herdadas do perfil:</strong> Este usuário herda todas as permissões do perfil selecionado, podendo ter exceções específicas na aba "Exceções".
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <div className="avatar avatar-lg bg-warning-light mx-auto mb-3">
                              <i className="ti ti-user-exclamation fs-2 text-warning"></i>
                            </div>
                            <h6 className="text-warning">Usuário sem perfil definido</h6>
                            <p className="text-muted">Este usuário não possui um perfil base. É recomendado definir um perfil para facilitar a gestão de permissões.</p>
                            <button 
                              className="btn btn-warning"
                              onClick={() => {
                                // Aqui você pode abrir o modal de definir perfil
                                onClose();
                                // Implementar lógica para abrir modal de definição de perfil
                              }}
                            >
                              <i className="ti-plus me-1"></i>
                              Definir Perfil
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'excecoes' && (
              <div className="tab-pane active">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0">⚠️ Exceções de Permissão Ativas ({excecoes.length})</h6>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      if (onAbrirExcecoes) {
                        onClose();
                        onAbrirExcecoes(usuario);
                      }
                    }}
                  >
                    <i className="ti-plus me-1"></i>
                    Adicionar Exceção
                  </button>
                </div>

                {loadingExcecoes ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status"></div>
                    <p className="mt-2">Carregando exceções...</p>
                  </div>
                ) : excecoes.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Módulo</th>
                          <th>Ação</th>
                          <th>Permissão</th>
                          <th>Motivo</th>
                          <th>Data</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excecoes.map(excecao => (
                          <tr key={excecao.id}>
                            <td>
                              <span className={`badge ${excecao.tipo_permissao === 'ADICIONAR' ? 'bg-success' : 'bg-danger'}`}>
                                {excecao.tipo_permissao === 'ADICIONAR' ? '✅ ADICIONAR' : '❌ REMOVER'}
                              </span>
                            </td>
                            <td>{excecao.modulo_nome || excecao.modulo_codigo}</td>
                            <td>{excecao.acao_nome || excecao.acao_codigo}</td>
                            <td>
                              {excecao.permitido ? (
                                <span className="text-success">Permitido</span>
                              ) : (
                                <span className="text-danger">Negado</span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {excecao.motivo || 'Sem motivo especificado'}
                              </small>
                            </td>
                            <td>
                              <small>{formatDate(excecao.data_criacao)}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoverExcecao(excecao.id)}
                                title="Remover exceção"
                              >
                                <i className="ti ti-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="avatar avatar-lg bg-success-light mx-auto mb-3">
                      <i className="ti ti-check-circle fs-2 text-success"></i>
                    </div>
                    <h6>Nenhuma exceção configurada</h6>
                    <p className="text-muted">Este usuário segue exatamente as permissões do seu perfil base.</p>
                  </div>
                )}
              </div>
            )}

          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};